<script setup lang="ts">

import { ref, watch } from "vue";
import { IObjectArray } from "@/interfaces/IObjectArray.ts";

type IProps = {
  list: IObjectArray<any>;
  showSelected?: boolean;
  placeholder?: string;
  label?: string;
  disabled?: boolean;
}

type ListItemInternal = {
  key: string;
  value: any;
  isSelected: boolean;
}

const model = defineModel<string>({
  required: true
});

const props = withDefaults(defineProps<IProps>(), {
  showSelected: false,
  placeholder: "",
  label: "",
  disabled: false
});


function getInternalListFromProps(list: IObjectArray<any>, selectedKey: string | null): ListItemInternal[] {
  return Object.keys(list).reduce((accum: ListItemInternal[], key: string) => {
    const isSelected = key === selectedKey;

    if (!props.showSelected && isSelected) {
      return accum;
    }

    accum.push({
      value: list[key],
      key,
      isSelected
    });

    return accum;
  }, []);
}

const list = ref<ListItemInternal[]>(getInternalListFromProps(props.list, model.value));

function getValueByKey(values: IObjectArray<any>, key: string): any | null {
  if (values.hasOwnProperty(key)) {
    return values[key];
  }

  return null;
}

function handleItemSelect(key: string): void {
  model.value = key;
  isOpen.value = false;
}

const currentValue = ref<any>(getValueByKey(props.list, model.value));
watch(model, (value: string) => {
  currentValue.value = getValueByKey(props.list, value);
  list.value = getInternalListFromProps(props.list, model.value);
});

const isOpen = ref(false);

function getValue(value: any) {
  if (typeof value === "string") {
    return value;
  } else if (typeof value === "object") {
    if (value.hasOwnProperty("value")) {
      return value.value;
    } else if (value.hasOwnProperty("name")) {
      return value.name;
    } else {
      return value[Object.keys(value)[0]];
    }
  }
}
</script>

<template>
  <div class="w-full">
    <label v-if="props.label" class="text-gray-900">
      {{ props.label }}
    </label>
    <div class="relative">
      <button type="button"
              @click="isOpen = !isOpen"
              class="relative w-full h-[54px] cursor-pointer rounded-lg px-2 py-4 bg-[var(--m-neutral-200)] hover:bg-[var(--m-neutral-300)] text-left shadow-sm sm:text-sm sm:leading-6"
              :class="{'text-[var(--m-neutral-800)]': !currentValue, 'text-[var(--m-dark)]': currentValue}"
              aria-haspopup="listbox" aria-expanded="true" aria-labelledby="listbox-label">
        <slot name="selected" :item="currentValue">
          {{ currentValue || props.placeholder }}
        </slot>
        <span class="pointer-events-none absolute inset-y-0 right-2 ml-3 flex items-center pr-2">
          <svg class="h-5 w-5 text-gray-400 arrow-down" :class="{'open': isOpen}" viewBox="0 0 20 12"
               fill="currentColor" aria-hidden="true">
            <path
              d="M9.99961 11.1C9.77461 11.1 9.58711 11.025 9.39961 10.875L0.774609 2.39995C0.437109 2.06245 0.437109 1.53745 0.774609 1.19995C1.11211 0.862451 1.63711 0.862451 1.97461 1.19995L9.99961 9.03745L18.0246 1.12495C18.3621 0.787451 18.8871 0.787451 19.2246 1.12495C19.5621 1.46245 19.5621 1.98745 19.2246 2.32495L10.5996 10.8C10.4121 10.9875 10.2246 11.1 9.99961 11.1Z"
              fill="currentColor" />
          </svg>
        </span>
      </button>


      <!--
        Select popover, show/hide based on select state.

        Entering: ""
          From: ""
          To: ""
        Leaving: "transition ease-in duration-100"
          From: "opacity-100"
          To: "opacity-0"
      -->
      <transition
        enter-active-class="transition-all duration-200 ease-out"
        leave-active-class="transition-all duration-750 ease-in"
        enter-class="opacity-0 scale-75"
        enter-to-class="opacity-100 scale-100"
        leave-class="opacity-100 scale-100"
        leave-to-class="opacity-0 scale-75"
      >
        <ul v-if="isOpen"
            class="absolute z-10 mt-1 max-h-56 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm"
            tabindex="-1" role="listbox" aria-labelledby="listbox-label" aria-activedescendant="listbox-option-3">
          <!--
            Select option, manage highlight styles based on mouseenter/mouseleave and keyboard navigation.

            Highlighted: "bg-indigo-600 text-white", Not Highlighted: "text-gray-900"
          -->
          <li
            v-for="item in list"
            class="relative cursor-pointer select-none py-2 pl-3 pr-9 text-gray-900 hover:bg-indigo-600 hover:text-white"
            :id="'listbox-option-' + item.key"
            role="option"
            @click="() => handleItemSelect(item.key)"
          >
            <div class="flex items-center">
              <slot name="item" :item="item.value">
                <span class="ml-3 block truncate"
                      :class="{'font-normal': !item.isSelected,'font-semibold': item.isSelected}">
                  {{ getValue(item.value) }}
                </span>
              </slot>
            </div>

            <span v-if="item.isSelected" class="absolute inset-y-0 right-0 flex items-center pr-4 text-indigo-600">
            <svg class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fill-rule="evenodd"
                    d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"
                    clip-rule="evenodd" />
            </svg>
          </span>
          </li>
        </ul>
      </transition>

      <button
        type="button"
        v-if="isOpen"
        @click="isOpen = false" class="fixed top-0 left-0 h-full w-full"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
.arrow-down {
  transform: rotate(0deg);
  transition: transform 0.2s linear;
}

.arrow-down.open {
  transform: rotate(180deg);
  transition: transform 0.2s linear;
}
</style>
